import { useEffect, useState } from "react";
import { useFormik } from "formik";
import { Box, Stack } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import compInfos from "./add-new-gateway-form.json";
import { useLangContext } from "../../context/langContext";
import { number, object, string } from "yup";
import MuiTextField from "../../components/common/text-field/MuiTextField";
import { useContactsContext } from "../../context/contactsContext";
import { useProtectedAxiosInstance } from "../../hooks/axiosInstance";
import errorResolver from "../../utils/error-resolver";
import { useNotificationContext } from "../../context/notificationContext";
// import { PbxTrunkCreateRequest } from "../../services/Endpoints/pbxTrunk/PbxTrunkCreateEndpoint";
// import { PbxTrunkUpdateRequest } from "../../services/Endpoints/pbxTrunk/PbxTrunkUpdateEndpoint";
import { GatewaySchema } from "../../services/Schemas/Gateway";
import { GatewayCreateRequest } from "../../services/Endpoints/gateway";
import MuiSelect from "../../components/common/select/MuiSelect";

const successMsg = {
  fa: "درگاه ایجاد/آپدیت شد.",
  en: "New contact has been added.",
};

export interface IAddNewTrunkFormProps {
  onClose: () => void;
  handleReload?: () => void;
  serverInfo?: GatewaySchema;
}

const options = {
  status: [
    { label: "فعال", value: "active" },
    {
      label: "غیرفعال",
      value: "inactive",
    },
  ],
  driver: [
    { label: "زرین پال", value: "zarinpal" },
    {
      label: "پی استار",
      value: "pay_star",
    },
  ],
};

export default function AddNewGatewayForm({
  onClose,
  serverInfo,
  handleReload,
}: Readonly<IAddNewTrunkFormProps>) {
  const [waiting, setWaiting] = useState(false);
  useContactsContext();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const { lang } = useLangContext();

  const infos = compInfos[lang];
  const loginSchema = object({
    caption: string().required(),
    url_secret_key: string().required(),
    gateway_merchant_id: string().required(),
    image_url: string().required(),
    driver: string().required(),
    secret: string().optional(),
    status: string().required(),
    api_url: string().required(),
  });

  const formik = useFormik({
    initialValues: {
      caption: serverInfo?.caption ?? "",
      url_secret_key: serverInfo?.url_secret_key ?? "",
      api_url: serverInfo?.api_url ?? "",
      gateway_merchant_id: serverInfo?.gateway_merchant_id ?? "",
      image_url: serverInfo?.image_url ?? "",
      driver: serverInfo?.driver ?? "zarinpal",
      status: serverInfo?.status ?? "active",
      secret: serverInfo?.secret ?? "",
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setWaiting(true);
      const serverCreateRequest = new GatewayCreateRequest(protectedInstance);

      try {
        const {
          driver,
          gateway_merchant_id,
          image_url,
          status,
          url_secret_key,
          caption,
          api_url,
          secret
        } = values;

        const resp = !serverInfo
          ? await serverCreateRequest.send({
              caption,
              driver,
              gateway_merchant_id,
              image_url,
              status,
              url_secret_key,
              api_url,
              secret
            })
          : await serverCreateRequest.update(serverInfo.id.toString(), {
              caption,
              driver,
              gateway_merchant_id,
              image_url,
              status,
              url_secret_key,
              secret,
              api_url,
            });

        if (resp.success) {
          setNotification({
            type: "success",
            message: resp.message || successMsg[lang],
          });
        } else {
          setNotification({
            type: "error",
            message: resp.message || "خطا در عملیات",
          });
        }

        setWaiting(false);
        handleReload && handleReload();
        formik.resetForm();
        onClose();
        setNotification({ type: "success", message: successMsg[lang] });
      } catch (error: any) {
        errorResolver({ error, setNotification });
        setWaiting(false);
      }
    },
    enableReinitialize: true,
  });
  useEffect(() => {
    console.log(formik.values);
  }, [formik.values]);
  return (
    <Stack
      maxWidth="100%"
      width="100%"
      gap="16px"
      mx="auto"
      component="form"
      onSubmit={formik.handleSubmit}
    >
      {infos.inputs.map(({ label, name, placeholder }) => {
        if (name === "driver" || name === "status") {
          return null;
        }

        return (
          <MuiTextField
            key={name}
            width="100%"
            label={label}
            placeholder={placeholder}
            name={name}
            inputMode={name === "phone" ? "numeric" : "text"}
            type="text"
            value={formik.values[name as "caption"]}
            handleChange={formik.handleChange}
            multiline={name === "description"}
            error={
              formik.touched[name as "caption"] &&
              Boolean(formik.errors[name as "caption"]) &&
              formik.errors[name as "caption"]
            }
          />
        );
      })}

      <Box display="flex" gap={2} width="100%">
        <MuiSelect
          label={infos.inputs.find((i) => i.name === "driver")?.label}
          name="driver"
          placeholder={
            infos.inputs.find((i) => i.name === "driver")?.placeholder
          }
          value={formik.values.driver}
          handleChange={formik.handleChange}
          options={options.driver}
          sxStyle={{ flex: 1 }}
        />
        <MuiSelect
          label={infos.inputs.find((i) => i.name === "status")?.label}
          name="status"
          placeholder={
            infos.inputs.find((i) => i.name === "status")?.placeholder
          }
          value={formik.values.status}
          handleChange={formik.handleChange}
          options={options.status}
          sxStyle={{ flex: 1 }}
        />
      </Box>
      <LoadingButton
        variant="confirm"
        sx={{
          mt: "10px",
        }}
        type="submit"
        loading={waiting}
      >
        {infos.buttonText}
      </LoadingButton>
    </Stack>
  );
}
